import RAPIER from "@dimforge/rapier3d-compat";
import * as THREE from "three";
import { roundedBox, standardMaterial } from "../render/materials";
import { PLAYER_GROUPS } from "../physics/layers";

export class PlayerController {
  readonly root = new THREE.Group();
  readonly body: RAPIER.RigidBody;
  readonly collider: RAPIER.Collider;
  readonly controller: RAPIER.KinematicCharacterController;
  yaw = 0;
  pitch = 0;
  verticalVelocity = 0;
  grounded = false;
  enabled = false;
  controlMode: "on-foot" | "seated" = "on-foot";
  #jumpReleased = true;
  readonly #keys = new Set<string>();

  get forwardAxis(): number {
    return Number(this.#keys.has("KeyW")) - Number(this.#keys.has("KeyS"));
  }

  get turnAxis(): number {
    return Number(this.#keys.has("KeyD")) - Number(this.#keys.has("KeyA"));
  }

  setControlMode(mode: "on-foot" | "seated"): void {
    this.controlMode = mode;
    this.#keys.clear();
    this.#jumpReleased = true;
    this.verticalVelocity = 0;
    this.grounded = false;
  }

  constructor(
    readonly world: RAPIER.World,
    readonly camera: THREE.PerspectiveCamera,
  ) {
    this.body = world.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(0, 2, 14),
    );
    this.collider = world.createCollider(
      RAPIER.ColliderDesc.capsule(0.5, 0.4).setCollisionGroups(PLAYER_GROUPS),
      this.body,
    );
    this.controller = world.createCharacterController(0.01);
    this.controller.enableAutostep(0.4, 0.25, true);
    this.controller.setMaxSlopeClimbAngle(Math.PI / 4);
    this.controller.enableSnapToGround(0.25);
    const coat = new THREE.Mesh(
      roundedBox(0.72, 1.05, 0.42, 0.15),
      standardMaterial(0x2d6870, 0.66),
    );
    coat.position.y = 0.95;
    const head = new THREE.Mesh(
      roundedBox(0.42, 0.42, 0.42, 0.16),
      standardMaterial(0xb77e62, 0.9),
    );
    head.position.y = 1.68;
    const legs = [-0.2, 0.2].map((x) => {
      const leg = new THREE.Mesh(
        roundedBox(0.2, 0.68, 0.24, 0.08),
        standardMaterial(0x182226, 0.85),
      );
      leg.position.set(x, 0.34, 0);
      return leg;
    });
    this.root.add(coat, head, ...legs);
    window.addEventListener("keydown", this.#onKeyDown);
    window.addEventListener("keyup", this.#onKeyUp);
    window.addEventListener("mousemove", this.#onMouseMove);
  }

  readonly #onKeyDown = (event: KeyboardEvent): void => {
    this.#keys.add(event.code);
  };
  readonly #onKeyUp = (event: KeyboardEvent): void => {
    this.#keys.delete(event.code);
    if (event.code === "Space") this.#jumpReleased = true;
  };
  readonly #onMouseMove = (event: MouseEvent): void => {
    if (!this.enabled || document.pointerLockElement === null) return;
    this.yaw -= event.movementX * 0.0022;
    this.pitch = THREE.MathUtils.clamp(
      this.pitch - event.movementY * 0.0018,
      (-70 * Math.PI) / 180,
      (70 * Math.PI) / 180,
    );
  };

  fixedUpdate(step: number): void {
    const translation = this.body.translation();
    if (!this.enabled) {
      this.root.position.set(translation.x, translation.y - 0.9, translation.z);
      return;
    }
    if (this.controlMode === "seated") {
      this.root.position.set(translation.x, translation.y - 0.9, translation.z);
      return;
    }
    const inputX = Number(this.#keys.has("KeyD")) - Number(this.#keys.has("KeyA"));
    const inputZ = Number(this.#keys.has("KeyS")) - Number(this.#keys.has("KeyW"));
    const input = new THREE.Vector3(inputX, 0, inputZ);
    if (input.lengthSq() > 0)
      input.normalize().applyAxisAngle(new THREE.Vector3(0, 1, 0), this.yaw);
    const speed = this.#keys.has("ShiftLeft") || this.#keys.has("ShiftRight") ? 7 : 4.5;
    if (this.#keys.has("Space") && this.grounded && this.#jumpReleased) {
      this.verticalVelocity = 5.2;
      this.#jumpReleased = false;
    }
    if (!this.grounded) this.verticalVelocity += -9.81 * step;
    const desired = {
      x: input.x * speed * step,
      y: this.verticalVelocity * step,
      z: input.z * speed * step,
    };
    this.controller.computeColliderMovement(this.collider, desired);
    const movement = this.controller.computedMovement();
    this.grounded = this.controller.computedGrounded();
    if (this.grounded && this.verticalVelocity < 0) this.verticalVelocity = 0;
    this.body.setNextKinematicTranslation({
      x: translation.x + movement.x,
      y: translation.y + movement.y,
      z: translation.z + movement.z,
    });
    this.root.position.set(translation.x, translation.y - 0.9, translation.z);
    if (input.lengthSq() > 0) this.root.rotation.y = Math.atan2(input.x, input.z);
    const focus = new THREE.Vector3(translation.x, translation.y + 0.75, translation.z);
    const offset = new THREE.Vector3(0, 2.2, 5).applyEuler(
      new THREE.Euler(this.pitch, this.yaw, 0, "YXZ"),
    );
    const desiredCamera = focus.clone().add(offset);
    const direction = desiredCamera.clone().sub(focus);
    const ray = new RAPIER.Ray(focus, direction.clone().normalize());
    const hit = this.world.castRay(
      ray,
      direction.length(),
      true,
      undefined,
      undefined,
      this.collider,
    );
    if (hit)
      desiredCamera
        .copy(focus)
        .add(direction.normalize().multiplyScalar(Math.max(0.45, hit.timeOfImpact - 0.25)));
    this.camera.position.lerp(desiredCamera, 0.25);
    this.camera.lookAt(focus);
  }

  dispose(): void {
    window.removeEventListener("keydown", this.#onKeyDown);
    window.removeEventListener("keyup", this.#onKeyUp);
    window.removeEventListener("mousemove", this.#onMouseMove);
    this.world.removeCharacterController(this.controller);
    this.world.removeRigidBody(this.body);
  }
}
